import { defineStore } from 'pinia'
import { computed, ref, Ref } from 'vue'
import Api from '@/config/api.helper'
import { Assinatura } from '@/types/assinatura/assinatura'
import { PlanosServicosCounter } from '@/types/planos'
import { useEmpresaStore } from './empresaStore'

interface AssinaturaStore {
  assinatura: Ref<Assinatura | null>
  planos_servicos_counter: Ref<PlanosServicosCounter[]>
  isLoading: Ref<boolean>
  hasAssinatura: Ref<boolean>
  showBlockedModal: Ref<boolean>
  blockedModalMotivo: Ref<string | undefined>
  setAssinatura(newAssinatura: Assinatura | null): void
  clearAssinatura(): void
  fetchAssinatura(empresaLogadaId?: string): Promise<Assinatura | null>
  updatePlanosServicosCounter(counters: PlanosServicosCounter[]): void
  initialize(): Promise<void>
  openBlockedModal(motivo?: string): void
  closeBlockedModal(): void
}

const ASSINATURA_STORAGE_KEY = 'assinaturaStore.assinatura'

function hasActiveSession(): boolean {
  const token = localStorage.getItem('token')
  return typeof token === 'string' && token.trim().length > 0
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function normalizeCounter(
  counter: PlanosServicosCounter
): PlanosServicosCounter | null {
  if (!isRecord(counter)) {
    return null
  }

  const usesCount = Number(counter.uses_count)

  if (
    Number.isNaN(usesCount)
  ) {
    return null
  }

  return counter
}

function normalizeAssinaturaPayload(payload: any): Assinatura | null {
  if (!isRecord(payload)) {
    return null
  }

  const id = Number(payload.uuid_ass_id)
  const criadoEm = String(payload.criado_em)

  const counters = Array.isArray(payload.planos_servicos_counter)
    ? payload.planos_servicos_counter
        .map((counter) => normalizeCounter(counter))
        .filter((counter): counter is PlanosServicosCounter => !!counter)
    : payload.planos_servicos_counter === null
      ? null
      : undefined

  return {
    id,
    uuid_ass_id: String(payload.uuid_ass_id),
    criado_em: criadoEm,
    status: Boolean(payload.status),
    lifetime: Boolean(payload.lifetime),
    bloqueada: Boolean(payload.bloqueada),
    bloqueada_motivo:
      typeof payload.bloqueada_motivo === 'string'
        ? payload.bloqueada_motivo
        : undefined,
    finalizada:
      typeof payload.finalizada === 'boolean' ? payload.finalizada : undefined,
    is_waiting_for_recover_plan_payment:
      typeof payload.is_waiting_for_recover_plan_payment === 'boolean'
        ? payload.is_waiting_for_recover_plan_payment
        : undefined,
    planos_servicos_counter: counters,
    is_waiting_for_finalization:
      typeof payload.ass_waiting_finalization === 'boolean'
        ? payload.ass_waiting_finalization
        : undefined,
    plano_agendado: isRecord(payload.plano_agendado)
      ? {
          pla_descricao: String(payload.plano_agendado.pla_descricao ?? ''),
          uuid_pla_id: String(payload.plano_agendado.uuid_pla_id ?? ''),
          data_plano_agendado: String(payload.plano_agendado.data_plano_agendado ?? ''),
        }
      : null,
  }
}

function extractAssinaturaPayload(responseData: unknown): unknown {
  if (!isRecord(responseData)) {
    return responseData
  }

  if ('assinatura' in responseData) {
    return responseData.assinatura
  }

  if ('data' in responseData) {
    const data = responseData.data

    if (isRecord(data) && 'assinatura' in data) {
      return data.assinatura
    }

    return data
  }

  return responseData
}

function getEmpresaLogadaHeader(): string | undefined {
  const empresaStore = useEmpresaStore()
  const uuidEmpId = empresaStore.empresa?.uuid_emp_id

  if (typeof uuidEmpId === 'string' && uuidEmpId) {
    return uuidEmpId
  }

  return undefined
}

export const useAssinaturaStore = defineStore(
  'assinatura',
  (): AssinaturaStore => {
    const assinatura = ref<Assinatura | null>(null)
    const planos_servicos_counter = ref<PlanosServicosCounter[]>([])
    const isLoading = ref<boolean>(false)
    const showBlockedModal = ref<boolean>(false)
    const blockedModalMotivo = ref<string | undefined>(undefined)

    const hasAssinatura = computed((): boolean => !!assinatura.value)

    function openBlockedModal(motivo?: string): void {
      blockedModalMotivo.value = motivo ?? assinatura.value?.bloqueada_motivo
      showBlockedModal.value = true
    }

    function closeBlockedModal(): void {
      showBlockedModal.value = false
    }

    function setAssinatura(newAssinatura: Assinatura | null): void {
      assinatura.value = newAssinatura
      planos_servicos_counter.value = newAssinatura?.planos_servicos_counter || []

      if (newAssinatura) {
        localStorage.setItem(
          ASSINATURA_STORAGE_KEY,
          JSON.stringify(newAssinatura)
        )
        return
      }

      localStorage.removeItem(ASSINATURA_STORAGE_KEY)
    }

    function clearAssinatura(): void {
      setAssinatura(null)
    }

    async function fetchAssinatura(
      empresaLogadaId?: string
    ): Promise<Assinatura | null> {
      if (!hasActiveSession()) {
        return null
      }

      isLoading.value = true

      try {
        const empresaLogada = empresaLogadaId ?? getEmpresaLogadaHeader()

        if (!empresaLogada) {
          console.warn(
            'Requisição de assinatura ignorada: uuid da empresa logada ausente para montar header empresalogada.'
          )
          return null
        }

        const response = await Api.get<any>(
          'v2/assinatura',
          {
            headers: {
              empresalogada: empresaLogada,
            },
          }
        )
        const payload = extractAssinaturaPayload(response.data ?? [])
        const normalizedAssinatura = normalizeAssinaturaPayload(payload)

        if (!normalizedAssinatura) {
          clearAssinatura()
          return null
        }

        setAssinatura(normalizedAssinatura)
        return normalizedAssinatura
      } catch (error) {
        //console.error('Erro ao buscar dados da assinatura:', error)
        return null
      } finally {
        isLoading.value = false
      }
    }

    function updatePlanosServicosCounter(
      counters: PlanosServicosCounter[]
    ): void {
      planos_servicos_counter.value = counters

      if (!assinatura.value) {
        return
      }

      setAssinatura({
        ...assinatura.value,
        planos_servicos_counter: counters,
      })
    }

    async function initialize(): Promise<void> {
      if (!hasActiveSession()) {
        return
      }

      let cachedAssinatura = localStorage.getItem(ASSINATURA_STORAGE_KEY)

      if (!cachedAssinatura) {
        await fetchAssinatura()
        cachedAssinatura = localStorage.getItem(ASSINATURA_STORAGE_KEY)

        if (!cachedAssinatura) {
          return
        }
      }

      try {
        const parsedAssinatura = JSON.parse(cachedAssinatura) as unknown
        const normalizedAssinatura = normalizeAssinaturaPayload(parsedAssinatura)

        if (!normalizedAssinatura) {
          clearAssinatura()
          await fetchAssinatura()
          return
        }

        assinatura.value = normalizedAssinatura
        planos_servicos_counter.value = normalizedAssinatura.planos_servicos_counter || []
      } catch (error) {
        console.error('Erro ao carregar dados da assinatura em cache:', error)
        clearAssinatura()
        await fetchAssinatura()
      }
    }

    return {
      assinatura,
      planos_servicos_counter,
      isLoading,
      hasAssinatura,
      showBlockedModal,
      blockedModalMotivo,
      setAssinatura,
      clearAssinatura,
      fetchAssinatura,
      updatePlanosServicosCounter,
      initialize,
      openBlockedModal,
      closeBlockedModal,
    }
  }
)